import Utils from '@/Core/Utils';
import {getAgentDetailBasedOnName} from '@/Core/agenium/agentDetails';
import {ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';
import {ApplicationData} from '@agenium-ai/agenium-client-sdk/dist/models';
import {AgeniumUser, Beneficiary, CalcfocusReportRequest} from '@amplify/athena-models';
import moment from 'moment';
import RootStore from '../../../Core/mobx/RootStore';
import {calcfocusAPI} from '../api/calcfocus';

const beneKeys: [string, keyof Beneficiary][] = [
    ['Case_BeneDetails_BeneficiaryType', 'type'],
    ['Case_BeneDetails_FirstName', 'firstName'],
    ['Case_BeneDetails_MiddleName', 'middleName'],
    ['Case_BeneDetails_LastName', 'lastName'],
    ['Case_BeneDetails_Relationship', 'relationship'],
    ['Case_BeneDetails_TrustName', 'trustName'],
    ['Case_BeneDetails_Percentage', 'percent'],
    ['Case_BeneDetails_BusinessName', 'businessName'],
    ['Case_BeneDetails_TaxID', 'taxId'],
    ['Case_BeneDetails_DOB', 'dateOfBirth'],
    ['Case_BeneDetails_SSN', 'ssn'],
];

const continBeneKeys: [string, keyof Beneficiary][] = [
    ['Case_BeneDetails_ContinBeneficiaryType', 'type'],
    ['Case_BeneDetails_ContinFirstName', 'firstName'],
    ['Case_BeneDetails_ContinMiddleName', 'middleName'],
    ['Case_BeneDetails_ContinLastName', 'lastName'],
    ['Case_BeneDetails_ContinRelationship', 'relationship'],
    ['Case_BeneDetails_ContinTrustName', 'trustName'],
    ['Case_BeneDetails_ContinPercentage', 'percent'],
    ['Case_BeneDetails_ContinBusinessName', 'businessName'],
    ['Case_BeneDetails_ContinTaxID', 'taxId'],
    ['Case_BeneDetails_ContinDOB', 'dateOfBirth'],
    ['Case_BeneDetails_ContinSSN', 'ssn'],
];

function getBeneficiaryData(applicationData: ApplicationData[]): Beneficiary[] {
    // pair the keys with their contingency status
    const applicationDataMap = new Map(applicationData.map((a) => [a.key, a.value]));

    // Case_BeneDetails_ContinBeneficiaryType_1, Case_BeneDetails_ContinBeneficiaryType_2
    const beneficiaries = [0, 1, 2, 3, 4, 5]
        .flatMap((i) =>
            (
                [
                    [beneKeys, false],
                    [continBeneKeys, true],
                ] as [string[][], boolean][]
            ).flatMap(
                // pair the keys with contingency value
                ([keys, isContingent]) => {
                    const beneficiary = {isContingent} as any;
                    let foundProperty = false;

                    keys.forEach(([sourceKey, destinationKey]) => {
                        const adjustedKey = i === 0 ? sourceKey : `${sourceKey}_${i}`;
                        const value = applicationDataMap.get(adjustedKey);
                        if (value != null) {
                            foundProperty = true;
                            if (destinationKey === 'dateOfBirth') {
                                const formattedDob = moment(value, ['MM/DD/YYYY', 'M/D/YYYY', 'YYYY-MM-DD']).format(
                                    'YYYY-MM-DD',
                                );
                                if (formattedDob === 'Invalid date') {
                                    console.log(`Invalid date input: ${value}`);
                                }
                                beneficiary[destinationKey] = formattedDob;
                            } else if (destinationKey === 'percent') {
                                beneficiary[destinationKey] = parseInt(value);
                            } else {
                                beneficiary[destinationKey] = value;
                            }
                        }
                    });
                    // assume we found a complete beneficiary if we found one matching nonnull value for it.
                    return foundProperty ? (beneficiary as Beneficiary) : null;
                },
            ),
        )
        .filter((b) => !!b) as Beneficiary[];

    return beneficiaries;
}

const overrideProsperAgentNumber = (ageniumStoredAgentNumber: string, agentNameOnForm: string) => {
    if (ageniumStoredAgentNumber == '6016101' || ageniumStoredAgentNumber == null) {
        // this is a test account, or the Agent & Product Information is missing. Then, attempt to get agent email, and retrieve
        // agent number from our list
        var newAgentDetail = getAgentDetailBasedOnName(agentNameOnForm);
        if (newAgentDetail != null) {
            return newAgentDetail.Case_Agent_KeyedContractNumber;
        } else {
            return null;
        }
    } else {
        return ageniumStoredAgentNumber;
    }
};

const getReinsuranceData = (
    applicationData: ApplicationData[],
): CalcfocusReportRequest['prosperReinsurancePercent'] => {
    const [munich, rga, scor] = [
        'Case_UWDecision_MunichReinsurancePercent',
        'Case_UWDecision_RGAReinsurancePercent',
        'Case_UWDecision_SCORReinsurancePercent',
    ].map((key) => parseInt(applicationData.find((t) => t.key === key)?.value));
    return {munich, rga, scor};
};

// populates calcfocus request with data collected by agenium
export function getAgeniumCalcfocusRequest(
    applicationDataLocal: ApplicationData[],
    ageniumUser: AgeniumUser | null,
    savedRequest?: any,
): CalcfocusReportRequest {
    function getByKeyInApplicationData<T>(key: string, transformer: (value: string) => T): T;
    function getByKeyInApplicationData<T extends string = string>(key: string): T;
    function getByKeyInApplicationData(key: string): string;
    function getByKeyInApplicationData<T>(key: string, transformer?: (value: string) => T): T | string {
        const value = applicationDataLocal?.find((t) => t.key === key)?.value;
        return transformer ? transformer(value) : value;
    }

    const {parameterStore} = RootStore.agentPortalStore;
    var prosperAgentNumber = ageniumUser
        ? ageniumUser.keyedContractNumber
        : overrideProsperAgentNumber(
              getByKeyInApplicationData('Case_AgentReport_AgentAssistedContractNumber')
                  ? getByKeyInApplicationData('Case_AgentReport_AgentAssistedContractNumber')
                  : getByKeyInApplicationData('Case_Agent_KeyedContractNumber'),
              parameterStore.approval.agentName.value,
          );

    const accountTypeParam = getByKeyInApplicationData('Case_Bank_AccountType');
    const formattedAccountType = accountTypeParam === 'Savings' ? 'SAVINGS' : 'CHECKING';

    if (!parameterStore.applicationId) {
        throw new Error('Application ID is missing in parameter store');
    }

    if (!parameterStore.instantIulApplicationId) {
        throw new Error('Instant IUL Application ID is missing in parameter store');
    }

    // Quote configuration source: use savedRequest exclusively if provided, otherwise use parameterStore.
    // This prevents intermingling of values between the two sources.
    const quoteSource = savedRequest ?? {
        prosperAmplifySolves: parameterStore.quote.prosperAmplifySolves.value,
        prosperSpecify: parameterStore.quote.prosperSpecify.value,
        prosperAmount: parameterStore.quote.prosperAmount.value as number,
        prosperIllustrationRequest: parameterStore.quote.prosperIllustrationRequest.value,
        prosperFaceAmountType: parameterStore.quote.prosperFaceAmountType.value,
        prosperFaceAmount: parameterStore.quote.prosperFaceAmount.value,
        prosperDeathBenefitChangeAge: parameterStore.quote.prosperDeathBenefitChangeAge.value,
        prosperPremiumType: parameterStore.quote.prosperPremiumType.value,
        prosperPremium: parameterStore.quote.prosperPremium.value,
        prosperPremiumMode: parameterStore.quote.prosperPremiumMode.value,
        prosperLumpSum: parameterStore.quote.prosperLumpSum.value,
        prosperPremiumEndYear: parameterStore.quote.prosperPremiumEndYear.value,
        prosperIndexName: parameterStore.quote.prosperIndexName.value,
        prosperIndexedAllocationPercent: parameterStore.quote.prosperIndexedAllocationPercent.value,
        prosperIndexedAccountRateOfReturn: parameterStore.quote.prosperIndexedAccountRateOfReturn.value,
        prosperDistributionAmount: parameterStore.quote.prosperDistributionAmount.value,
        prosperDistributionSolveType: parameterStore.quote.prosperDistributionSolveType.value,
        prosperDistributionType: parameterStore.quote.prosperDistributionType.value,
        prosperDistributionStartAge: parameterStore.quote.prosperDistributionStartAge.value,
        prosperDistributionEndAge: parameterStore.quote.prosperDistributionEndAge.value,
        prosperAllowMEC: parameterStore.quote.prosperAllowMEC.value,
        prosperSolveGoal: parameterStore.quote.prosperSolveGoal.value,
        prosperSolveAge: parameterStore.quote.prosperSolveAge.value,
    };

    const inputData: CalcfocusReportRequest = {
        ageniumCaseId: parameterStore.applicationId,
        applicationId: parameterStore.instantIulApplicationId,
        firstName: getByKeyInApplicationData('Case_Applicant_FirstName'),
        lastName: getByKeyInApplicationData('Case_Applicant_LastName'),
        phoneNumber: getByKeyInApplicationData('Case_Applicant_PhoneNumber'),
        dob: getByKeyInApplicationData('Case_RateCalc_DateOfBirth', (v: string) => new Date(v).toISOString()),
        age: getByKeyInApplicationData('Case_Applicant_AgeVMK', (v: string) => parseInt(v)),
        gender: getByKeyInApplicationData(
            'Case_RateCalc_Gender',
            (v: string) => (({MALE: 'male', FEMALE: 'female'}) as const)[v],
        ) as any,
        ssn: getByKeyInApplicationData('Case_Applicant_SSN'),
        address: getByKeyInApplicationData('Case_Applicant_AddressLine1'),
        state: getByKeyInApplicationData('Case_Applicant_State'),
        zip: getByKeyInApplicationData('Case_Applicant_PostalCode'),
        city: getByKeyInApplicationData('Case_Applicant_City'),
        emailAddress: getByKeyInApplicationData('Case_Applicant_EmailAddress'),
        prosperSmoker: getByKeyInApplicationData('Case_RateCalc_TobaccoUse', (v: string) => v !== 'NONSMOKER'),
        prosperAmplifySolves: quoteSource.prosperAmplifySolves,
        prosperSpecify: quoteSource.prosperSpecify,
        prosperAmount: quoteSource.prosperAmount || '',
        prosperIllustrationRequest: quoteSource.prosperIllustrationRequest,
        prosperRiskClass: getByKeyInApplicationData(
            'Case_UWDecision_Overall',
        ) as CalcfocusReportRequest['prosperRiskClass'],
        prosperTableRating: getByKeyInApplicationData('Case_UWDecision_HxOutcome_TableRating', (v) =>
            v === null ? 'NotReady' : v,
        ) as CalcfocusReportRequest['prosperTableRating'],
        prosperFlatExtra: getByKeyInApplicationData('Case_UWDecision_FlatExtraTotalRatePer1000Final', (v) =>
            parseInt(v),
        ),
        prosperFlatExtraDuration: getByKeyInApplicationData('Case_UWDecision_FlatExtraDuration', (v) =>
            v === 'Permanent' ? 'M' : parseInt(v),
        ),
        prosperFaceAmountType: quoteSource.prosperFaceAmountType,
        prosperFaceAmount: quoteSource.prosperFaceAmount,
        prosperDeathBenefitOption: getByKeyInApplicationData(
            'Case_RateCalc_DeathBenefitOption',
            (v: string) => (({LEVEL: 'ALevel', INCREASING: 'BIncreasing'}) as const)[v],
        ) as any,
        prosperDeathBenefitChangeAge: getByKeyInApplicationData('Case_RateCalc_DeathBenefitOption', (v) =>
            v === 'INCREASING' ? quoteSource.prosperDeathBenefitChangeAge || 66 : undefined,
        ),
        prosperPremiumType: quoteSource.prosperPremiumType,
        prosperPremium: quoteSource.prosperPremium,
        prosperPremiumMode: quoteSource.prosperPremiumMode,
        prosperLumpSum: quoteSource.prosperLumpSum,
        prosperPremiumEndYear: quoteSource.prosperPremiumEndYear,
        prosperIndexName: quoteSource.prosperIndexName,
        prosperIndexedAllocationPercent: quoteSource.prosperIndexedAllocationPercent,
        prosperIndexedAccountRateOfReturn: quoteSource.prosperIndexedAccountRateOfReturn,
        prosperDistributionAmount: quoteSource.prosperDistributionAmount,
        prosperDistributionSolveType: quoteSource.prosperDistributionSolveType,
        prosperDistributionType:
            quoteSource.prosperDistributionType === '0' ? undefined : quoteSource.prosperDistributionType,
        prosperDistributionStartAge: quoteSource.prosperDistributionStartAge,
        prosperDistributionEndAge: quoteSource.prosperDistributionEndAge,
        prosperAllowMEC: quoteSource.prosperAllowMEC,
        prosperDefinitionOfLifeInsurance: getByKeyInApplicationData(
            'Case_Applicant_DeathBenefitQualification',
            (v: string) => (v.includes('CVAT') ? 'CVAT' : 'GPT'),
        ),
        prosperSolveGoal: quoteSource.prosperSolveGoal,
        prosperSolveAge: quoteSource.prosperSolveAge,
        prosperBankAccountNumber: getByKeyInApplicationData('Case_Bank_AccountNumber'),
        prosperBankRoutingNumber: getByKeyInApplicationData('Case_Bank_RoutingNumber'),
        prosperBankAccountType: formattedAccountType,
        prosperEffectiveDate: getByKeyInApplicationData('Case_Applicant_RequestedEffectiveDate') || undefined,
        prosperAgentNumber: prosperAgentNumber || undefined,
        prosperReinsurancePercent: getReinsuranceData(applicationDataLocal),
        beneficiaries: getBeneficiaryData(applicationDataLocal),
    };

    console.log('===========REQUEST WITH AGENIUM DATA============');
    console.log(inputData);
    console.log('================================================');

    return inputData;
}

// populates calcfocus request with data collected by agenium,
// but excludes certain data which could result in calcfocus not finding a rate for this
// declined policy.
export function getCalcfocusDeclineRequest(
    originalRequest: CalcfocusReportRequest,
    applicationDataLocal: ApplicationData[],
): CalcfocusReportRequest {
    const getByKeyInApplicationData = (key: string, responseModFn?: Function) =>
        responseModFn
            ? responseModFn(applicationDataLocal?.find((t) => t.key === key)?.value)
            : applicationDataLocal?.find((t) => t.key === key)?.value;

    const {parameterStore} = RootStore.agentPortalStore;
    var prosperAgentNumber = overrideProsperAgentNumber(
        getByKeyInApplicationData('Case_Agent_KeyedContractNumber'),
        parameterStore.approval.agentName.value,
    );
    const inputData: CalcfocusReportRequest = {
        ...originalRequest,
        ageniumCaseId: parameterStore.applicationId as string,
        applicationId: parameterStore.instantIulApplicationId as string,
        firstName: getByKeyInApplicationData('Case_Applicant_FirstName'),
        lastName: getByKeyInApplicationData('Case_Applicant_LastName'),
        phoneNumber: getByKeyInApplicationData('Case_Applicant_PhoneNumber'),
        address: getByKeyInApplicationData('Case_Applicant_AddressLine1'),
        state: getByKeyInApplicationData('Case_Applicant_State'),
        zip: getByKeyInApplicationData('Case_Applicant_PostalCode'),
        city: getByKeyInApplicationData('Case_Applicant_City'),
        emailAddress: getByKeyInApplicationData('Case_Applicant_EmailAddress'),
        prosperIllustrationRequest: true,
        beneficiaries: getBeneficiaryData(applicationDataLocal),
        ...(prosperAgentNumber != null && {
            prosperAgentNumber: prosperAgentNumber,
        }),
        prosperReinsurancePercent: getReinsuranceData(applicationDataLocal),
    };

    return inputData;
}

export async function getApprovalQuoteFromCalcfocus(
    applicationDataLocal: ApplicationData[],
    ageniumUser: AgeniumUser | null,
    savedProsperData?: any,
) {
    const inputData = savedProsperData
        ? getAgeniumCalcfocusRequest(applicationDataLocal, ageniumUser, savedProsperData)
        : getAgeniumCalcfocusRequest(applicationDataLocal, ageniumUser);
    const {parameterStore} = RootStore.agentPortalStore;
    return (await calcfocusAPI(inputData as any, parameterStore)) ?? {};
}

export function getAgeniumFieldsForDeal(applicationDataLocal?: ApplicationData[]) {
    const ageniumFieldsToCache = new Set([
        'Case_Applicant_FirstName',
        'Case_Applicant_City',
        'Case_Applicant_State',
        'Case_Applicant_AddressLine1',
        'Case_Applicant_PostalCode',
        'Case_UWDecision_OverallDateVMK',
        'Case_UWDecision_Overall',
        'Case_PolicyNumber',
        'Case_FinancialCalc_MaxFaceAmount',
        'Case_RateCalc_ProductCode',
        'Case_RateCalc_TobaccoUse',
        'Case_RateCalc_UnderwritingClass',
        'Case_UWDecision_Overall',
        'Case_UWDecision_HxOutcome_TableRating',
        'Case_UWDecision_Refer_Notes',
    ]);
    return applicationDataLocal?.filter((ad) => ageniumFieldsToCache.has(ad.key));
}

export function getApprovalFieldsForDeal(parameterStore: ParameterStore) {
    const bdsName = parameterStore.approval.bdsName.valueAsString();
    return {
        firstName: parameterStore.approval.firstName.valueAsString(),
        lastName: parameterStore.approval.lastName.valueAsString(),
        state: parameterStore.approval.state.valueAsString(),
        currentlyEmployed: parameterStore.approval.currentlyEmployed.valueAsString(),
        currentlySelfEmployed: (parameterStore.approval.currentSelfEmployed.value as boolean) ? 'true' : 'false',
        dateOfBirth: `${parameterStore.approval.bmonth.value}-${parameterStore.approval.bday.value}-${parameterStore.approval.byear.value}`,
        occupation: parameterStore.approval.occupation.valueAsString(),
        newXferOwner: RootStore.agentsList.find((o) => Utils.MakeVarName(o) === bdsName) ?? bdsName,
    };
}

export function mapParamStoreValueToAgenium(ageniumKey: String, value: string | number) {
    switch (ageniumKey) {
        case 'Case_Applicant_Citizenship': {
            if (value === 'Yes') {
                return 'U.S. Citizen';
            } else {
                return '';
            }
        }
        case 'Case_Applicant_DeathBenefit': {
            if (value === 'BIncreasing') {
                return 'Increasing Death Benefit';
            } else if (value === 'ALevel') {
                return 'Level Death Benefit';
            } else {
                return '';
            }
        }
        case 'Case_Applicant_DeathBenefitQualification': {
            if (value === 'CVAT') {
                return 'Cash Value Accumulation Test (CVAT)';
            } else if (value === 'GPT') {
                return 'Guideline Premium Test (GPT)';
            } else {
                return '';
            }
        }
    }
}
